import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Button,
  Steps,
  Upload,
  message,
  Alert,
  Tag,
  Tooltip,
  Tabs,
  Table,
  Empty,
} from "antd";
import {
  InboxOutlined,
  CloudUploadOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  CloseCircleFilled,
  ThunderboltFilled,
} from "@ant-design/icons";
import smartMapperApis, {
  AnalyseResponse,
  ConfirmReviewResponse,
  DownloadResult,
  JobResponse,
  MappingRow,
  SmartMapperTemplate,
} from "../../../../../api/smartMapper/smartMapperApis";
import MappingReviewTable from "./MappingReviewTable";
import { useSmartMapperJob } from "./useSmartMapperJob";

const { Dragger } = Upload;

// Template fallback when no site context is provided.
//   Villa segment → 002, Flat/Tower segment → 003, else → 001.
// BulkMaterialUpload computes this from the site's segmentLevel and
// passes it in as the `templateId` prop; we default to 001 here so the
// panel still works in isolation (e.g. in tests / Storybook).
const DEFAULT_TEMPLATE_ID = "001";

type WizardStep = "upload" | "analysing" | "review" | "transforming" | "done";

interface Props {
  /** Closes the parent overlay once user clicks Done. */
  onClose?: () => void;
  /** Site context surfaced in the banner + sent to /analyse. */
  siteId?: string | number;
  siteName?: string | null;
  segmentLevel?: string | null;
  segmentLabel?: string;
  templateId?: string;
}

const SmartMapperPanel: React.FC<Props> = ({
  onClose,
  siteId,
  siteName,
  segmentLevel,
  segmentLabel,
  templateId,
}) => {
  // Use the site-resolved template ID when present; fall back to 001.
  const effectiveTemplateId = templateId || DEFAULT_TEMPLATE_ID;
  const [step, setStep] = useState<WizardStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [template, setTemplate] = useState<SmartMapperTemplate | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<MappingRow[]>([]);
  const [unmappedTarget, setUnmappedTarget] = useState<string[]>([]);
  const [sourceFile, setSourceFile] = useState<string>("");
  const [sourceRowCount, setSourceRowCount] = useState<number | null>(null);
  const [mappingNotes, setMappingNotes] = useState<string[]>([]);

  const [analyseError, setAnalyseError] = useState<string | null>(null);
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [transformError, setTransformError] = useState<string | null>(null);
  const [downloadResult, setDownloadResult] = useState<DownloadResult | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Tracks whether we've already started the transform after confirmation,
  // so the polling effect on `confirmed → transformed` doesn't double-fire.
  const transformStartedRef = useRef(false);

  const { job, error: jobError, isPolling, start, stop, reset } = useSmartMapperJob();

  // Fetch template definition (field labels, required flags etc.). Re-runs
  // whenever the resolved templateId changes — e.g. user picked a
  // different site that flips the segment from Villa to Flat.
  useEffect(() => {
    let cancelled = false;
    smartMapperApis
      .getTemplates()
      .then((templates) => {
        if (cancelled) return;
        const t = templates?.find((x) => x.template_id === effectiveTemplateId);
        if (!t) {
          setTemplateError(`Template ${effectiveTemplateId} not available.`);
          return;
        }
        setTemplate(t);
      })
      .catch(() => {
        if (!cancelled) setTemplateError("Couldn't load template definition.");
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveTemplateId]);

  // ─── Transitions driven by mapping_status (§6 GET /jobs poll) ─────────
  // The polling hook (useSmartMapperJob) calls §6 on a 2.5s tick; this
  // effect reacts to each fresh status and drives the wizard forward.
  useEffect(() => {
    if (!job) return;
    const s = job.mapping_status;
    if (s === "suggested" && step === "analysing") {
      // Slow path — analyse finished, fetch suggested mapping for review.
      void loadReviewMapping(job.job_id);
    } else if (s === "transformed" && step === "transforming") {
      // Transform finished — UI moves to "done"; download is user-triggered.
      setStep("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, step]);

  // §7 — GET /confirm/{jobId} (slow path only)
  const loadReviewMapping = async (id: string) => {
    try {
      const res: ConfirmReviewResponse = await smartMapperApis.getConfirm(id);
      setMapping(res.current_mapping || []);
      setUnmappedTarget(res.unmapped_target || []);
      setSourceFile(res.source_file);
      setSourceRowCount(res.source_row_count);
      setStep("review");
    } catch (err: any) {
      setAnalyseError(err?.message || "Could not load mapping for review");
    }
  };

  // ─── Step 1 — Upload & analyse ────────────────────────────────────────
  const handleUploadFile = (f: File) => {
    setFile(f);
    return false; // prevent AntD from auto-uploading
  };

  const startAnalyse = useCallback(async () => {
    if (!file) {
      message.warning("Please choose a file to upload.");
      return;
    }
    setAnalyseError(null);
    setStep("analysing");
    setMappingNotes([]);
    transformStartedRef.current = false;
    try {
      // §5 — POST /analyse/{template_id}?siteId={siteId}
      // Template is resolved from the site's segment level:
      //   Villa → 002, Flat/Tower → 003, else → 001.
      const res: AnalyseResponse = await smartMapperApis.analyse(
        effectiveTemplateId,
        file,
        siteId != null && siteId !== "" ? { siteId } : undefined
      );
      const id = res?.job_id;
      if (!id) throw new Error("Server didn't return a job_id");
      setJobId(id);
      setSourceFile(res?.source_file || file.name);
      setSourceRowCount(res?.source_row_count ?? null);
      setMappingNotes(res?.notes || []);

      // §6 — Always poll /jobs/{jobId} until mapping_status === "suggested".
      // (Even when analyse returns 200 with the mapping inline, we still
      // poll so the user can review/edit the mapping in §7 before
      // confirming. No fast-path shortcut to /transform — that's what was
      // previously bypassing the review screen.)
      start(id, ["suggested", "failed"]);
    } catch (err: any) {
      setAnalyseError(err?.message || "Upload / analysis failed");
      setStep("upload");
    }
  }, [file, start, effectiveTemplateId, siteId]);

  // ─── Slow path — Step 4 (PUT /confirm) ────────────────────────────────
  // Slow path only. After the user reviews the mapping in step 3, send
  // their edits back so the server can flip mapping_status → "confirmed"
  // and (as a side effect) cache the layout for future fast-path uploads.
  const handleConfirmMapping = async () => {
    if (!jobId) return;
    const payload: MappingRow[] = mapping.map((m) => ({
      source: m.source,
      target: m.target,
      confidence: m.target ? 1.0 : 0.0,
      is_master_data: m.is_master_data,
    }));
    setConfirmSaving(true);
    try {
      // §8 — PUT /confirm/{jobId}
      await smartMapperApis.putConfirm(jobId, payload);
      // §9 — POST /transform
      await startTransform(jobId);
    } catch (err: any) {
      message.error(err?.message || "Couldn't save mapping");
    } finally {
      setConfirmSaving(false);
    }
  };

  // ─── Step 5 — POST /transform (used by both paths) ────────────────────
  // After transform fires, we resume polling §6 until mapping_status ===
  // "transformed". That second poll is what populates the job response
  // with `confirmed_mapping` + `validation_errors`, which the Done step
  // then renders as two tabs (Transformed Data / Validation Errors).
  const startTransform = async (id: string) => {
    if (transformStartedRef.current) return;
    transformStartedRef.current = true;
    setTransformError(null);
    setStep("transforming");
    try {
      // §9 — POST /transform
      await smartMapperApis.transform(id);

      // §6 — Poll /jobs/{id} until status === "transformed". "confirmed"
      // is a transient station on the way through, so we exclude it from
      // the terminal set here.
      start(id, ["transformed", "failed"]);
    } catch (err: any) {
      setTransformError(err?.message || "Transform request failed");
      setStep("review");
      transformStartedRef.current = false;
    }
  };

  // ─── Step 5 — Download ────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      const result = await smartMapperApis.download(jobId);
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
      setDownloadResult(result);
    } catch (err: any) {
      message.error(err?.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleStartOver = () => {
    stop();
    reset();
    setFile(null);
    setJobId(null);
    setMapping([]);
    setUnmappedTarget([]);
    setMappingNotes([]);
    setSourceFile("");
    setSourceRowCount(null);
    setAnalyseError(null);
    setTransformError(null);
    setDownloadResult(null);
    transformStartedRef.current = false;
    setStep("upload");
  };

  // ─── Render helpers ───────────────────────────────────────────────────
  const stepIndex = useMemo(() => {
    switch (step) {
      case "upload":
        return 0;
      case "analysing":
        return 1;
      case "review":
        return 2;
      case "transforming":
        return 3;
      case "done":
        return 3;
      default:
        return 0;
    }
  }, [step]);

  const allRequiredMapped = useMemo(() => {
    if (!template) return false;
    const requiredNames = template.fields
      .filter((f) => f.required)
      .map((f) => f.name);
    const mappedTargets = new Set(
      mapping.map((m) => m.target).filter(Boolean) as string[]
    );
    return requiredNames.every((n) => mappedTargets.has(n));
  }, [mapping, template]);

  return (
    <div
      style={{
        padding: "4px 0",
        display: "block",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Site context banner — surfaces which site this upload is scoped
          to, what segment it is, and which intake template will be used.
          Helps users sanity-check before they upload a 100k-row workbook. */}
      {(siteName || segmentLevel || templateId) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "center",
            padding: "10px 14px",
            marginBottom: 12,
            background: "#f8fafc",
            border: "1px solid #e6e8eb",
            borderRadius: 10,
            fontSize: 12,
          }}
        >
          {siteName ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  fontSize: 10.5,
                  fontWeight: 600,
                }}
              >
                Site
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color: "#0f172a",
                  maxWidth: 260,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={siteName}
              >
                {siteName}
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  fontSize: 10.5,
                  fontWeight: 600,
                }}
              >
                Site
              </span>
              <span style={{ color: "#94a3b8" }}>Not selected</span>
            </div>
          )}

          <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontSize: 10.5,
                fontWeight: 600,
              }}
            >
              Segment
            </span>
            <Tag
              color={
                (segmentLevel || "").toLowerCase() === "villa"
                  ? "purple"
                  : ["flat", "tower"].includes(
                      (segmentLevel || "").toLowerCase()
                    )
                  ? "blue"
                  : "default"
              }
              style={{ borderRadius: 4, margin: 0 }}
            >
              {segmentLabel || "Generic"}
            </Tag>
          </div>

          <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontSize: 10.5,
                fontWeight: 600,
              }}
            >
              Template
            </span>
            <Tag color="geekblue" style={{ borderRadius: 4, margin: 0 }}>
              {effectiveTemplateId}
              {template?.template_name ? ` · ${template.template_name}` : ""}
            </Tag>
          </div>
        </div>
      )}

      <Steps
        current={stepIndex}
        size="small"
        style={{ marginBottom: 16 }}
        items={[
          { title: "Upload" },
          { title: "Analyse" },
          { title: "Review mapping" },
          { title: "Done" },
        ]}
      />

      {templateError && (
        <Alert
          type="error"
          message={templateError}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}

      {step === "upload" && (
        <div
          className="smart-mapper-step-upload"
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 12,
          }}
        >
          <Dragger
            multiple={false}
            maxCount={1}
            beforeUpload={handleUploadFile}
            onRemove={() => setFile(null)}
            accept=".xls,.xlsx"
            fileList={
              file
                ? [
                    {
                      uid: "1",
                      name: file.name,
                      size: file.size,
                      type: file.type,
                      status: "done" as const,
                    },
                  ]
                : []
            }
            style={{ background: "#fafbfc", borderRadius: 10 }}
          >
            <div
              className="ant-upload-drag-icon"
              style={{ display: "block", marginBottom: 8 }}
            >
              <InboxOutlined
                style={{ color: "#2563eb", fontSize: 36 }}
              />
            </div>
            <div
              className="ant-upload-text"
              style={{
                display: "block",
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Drop your Excel here, or click to browse
            </div>
            <div
              className="ant-upload-hint"
              style={{ display: "block", fontSize: 12, color: "#64748b" }}
            >
              .xlsx or .xls · up to 100 MB · any column layout — the AI
              figures out the mapping
            </div>
          </Dragger>

          {analyseError && (
            <Alert
              type="error"
              message={analyseError}
              showIcon
              style={{ marginTop: 12 }}
            />
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 8,
              width: "100%",
              flexShrink: 0,
              marginTop: "8%",
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              disabled={!file || !template}
              onClick={startAnalyse}
            >
              Upload &amp; Map
            </Button>
          </div>
        </div>
      )}

      {step === "analysing" && (
        <ProgressCard
          title="Analysing your file"
          subtitle="The AI is reading the headers and figuring out the best mapping."
          notes={mappingNotes}
          jobMeta={{ jobId, sourceFile, sourceRowCount }}
          isPolling={isPolling}
          error={jobError}
          onCancel={handleStartOver}
        />
      )}

      {step === "review" && template && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 12,
          }}
        >
          <ReviewHeader
            sourceFile={sourceFile}
            sourceRowCount={sourceRowCount}
          />
          <MappingReviewTable
            template={template}
            mapping={mapping}
            unmappedTarget={unmappedTarget}
            onChange={setMapping}
          />
          {!allRequiredMapped && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12, borderRadius: 8 }}
              message="Some required fields aren't mapped"
              description="You can continue, but rows missing required values will appear as errors in the output."
            />
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              width: "100%",
              flexShrink: 0,
            }}
          >
            <Button
              icon={<ReloadOutlined />}
              onClick={handleStartOver}
              disabled={confirmSaving}
            >
              Start over
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleFilled />}
              loading={confirmSaving}
              onClick={handleConfirmMapping}
            >
              Confirm mapping &amp; transform
            </Button>
          </div>
        </div>
      )}

      {step === "transforming" && (
        <ProgressCard
          title="Transforming rows"
          subtitle="Applying your mapping, normalising dates and numbers, resolving materials against the catalog…"
          notes={mappingNotes}
          jobMeta={{ jobId, sourceFile, sourceRowCount }}
          isPolling={isPolling}
          error={jobError || transformError}
          onCancel={handleStartOver}
        />
      )}

      {step === "done" && job && (
        <DoneCard
          job={job}
          downloadResult={downloadResult}
          downloading={downloading}
          onDownload={handleDownload}
          onStartOver={handleStartOver}
          onClose={onClose}
        />
      )}
    </div>
  );
};

// ─── Subcomponents ────────────────────────────────────────────────────

const ReviewHeader: React.FC<{
  sourceFile: string;
  sourceRowCount: number | null;
}> = ({ sourceFile, sourceRowCount }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      background: "#fafbfc",
      border: "1px solid #e6e8ef",
      borderRadius: 10,
      marginBottom: 14,
    }}
  >
    <FileExcelOutlined style={{ fontSize: 20, color: "#16a34a" }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          color: "#0f172a",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {sourceFile}
      </div>
      <div style={{ fontSize: 11.5, color: "#64748b" }}>
        {sourceRowCount ?? "—"} rows detected · review the column mapping below
      </div>
    </div>
  </div>
);

// Glow-orb — circular gradient disc with the bolt, no spinner ring.
// Sits above the shimmer-label loader for a richer visual identity
// while keeping the Ask AI motion vocabulary.
const GlowOrbLoader: React.FC = () => (
  <div
    style={{
      position: "relative",
      width: 80,
      height: 80,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {/* Pulsing halo */}
    <motion.div
      aria-hidden
      animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.2, 0.55] }}
      transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity }}
      style={{
        position: "absolute",
        inset: -10,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(96, 165, 250, 0.65) 0%, rgba(244, 114, 182, 0.28) 55%, rgba(255,255,255,0) 75%)",
        filter: "blur(9px)",
      }}
    />

    {/* Gradient disc + bolt */}
    <motion.div
      animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
      transition={{ duration: 2.4, ease: "linear", repeat: Infinity }}
      style={{
        width: 60,
        height: 60,
        borderRadius: "50%",
        backgroundImage:
          "linear-gradient(135deg, #2563eb 0%, #38bdf8 30%, #f0abfc 60%, #fb923c 100%)",
        backgroundSize: "220% 220%",
        boxShadow:
          "0 6px 22px -4px rgba(37, 99, 235, 0.5), inset 0 1px 6px rgba(255,255,255,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.span
        animate={{
          scale: [1, 1.15, 1],
          opacity: [1, 0.7, 1],
        }}
        transition={{
          duration: 1.4,
          ease: "easeInOut",
          repeat: Infinity,
        }}
        style={{
          color: "#fff",
          fontSize: 26,
          lineHeight: 1,
          display: "inline-flex",
          filter: "drop-shadow(0 1px 3px rgba(15, 23, 42, 0.25))",
        }}
      >
        <ThunderboltFilled />
      </motion.span>
    </motion.div>
  </div>
);


const ProgressCard: React.FC<{
  title: string;
  subtitle: string;
  notes: string[];
  jobMeta: { jobId: string | null; sourceFile: string; sourceRowCount: number | null };
  isPolling: boolean;
  error: string | null;
  onCancel: () => void;
}> = ({ title, subtitle, notes, jobMeta, isPolling, error, onCancel }) => (
  <div
    style={{
      padding: "32px 24px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 16,
      background: "#fafbfc",
      border: "1px solid #e6e8ef",
      borderRadius: 12,
    }}
  >
    {error ? (
      <>
        <CloseCircleFilled style={{ fontSize: 36, color: "#dc2626" }} />
        <div style={{ textAlign: "center", maxWidth: 460 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            Something went wrong
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
              lineHeight: 1.55,
            }}
          >
            {error}
          </div>
        </div>
      </>
    ) : (
      <GlowOrbLoader />
    )}
    {!error && subtitle && (
      <div
        style={{
          textAlign: "center",
          maxWidth: 460,
          fontSize: 12.5,
          color: "#64748b",
          lineHeight: 1.55,
        }}
      >
        {subtitle}
      </div>
    )}
    {jobMeta.jobId && (
      <div style={{ display: "flex", gap: 6 }}>
        <Tag color="blue">{jobMeta.jobId}</Tag>
        {jobMeta.sourceRowCount != null && (
          <Tag>{jobMeta.sourceRowCount} rows</Tag>
        )}
        {isPolling && <Tag color="processing">polling…</Tag>}
      </div>
    )}
    {notes.length > 0 && !error && (
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          border: "1px solid #eef0f3",
          borderRadius: 8,
          padding: 10,
          fontSize: 12,
          color: "#475569",
        }}
      >
        {notes.slice(0, 4).map((n, i) => (
          <div key={i} style={{ marginBottom: i === notes.length - 1 ? 0 : 4 }}>
            • {n}
          </div>
        ))}
      </div>
    )}
    {error && (
      <Button onClick={onCancel}>Start over</Button>
    )}
  </div>
);

// Normalises whatever shape the backend returns for `validation_errors`
// into a flat array of rows that the AntD Table can render. The backend
// may send either:
//   - an array of { row, field, error_type, error, value } objects
//   - an object keyed by row index → array of error objects
//   - null / undefined when no errors
const normaliseValidationErrors = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    const out: any[] = [];
    Object.entries(raw).forEach(([rowKey, errs]) => {
      const list = Array.isArray(errs) ? errs : [errs];
      list.forEach((e: any) => {
        out.push({ row: e?.row ?? rowKey, ...e });
      });
    });
    return out;
  }
  return [];
};

const DoneCard: React.FC<{
  job: JobResponse;
  downloadResult: DownloadResult | null;
  downloading: boolean;
  onDownload: () => void;
  onStartOver: () => void;
  onClose?: () => void;
}> = ({ job, downloadResult, downloading, onDownload, onStartOver, onClose }) => {
  // Transformed Data tab — uses confirmed_mapping from the job response.
  const mappingRows = (job.confirmed_mapping || []).map((m, i) => ({
    key: i,
    ...m,
  }));

  // Validation Errors tab — uses validation_errors from the job response.
  const errorRows = normaliseValidationErrors(job.validation_errors).map(
    (e, i) => ({ key: i, ...e })
  );

  // Row counts. The JSON body of GET /jobs/{id} is the most reliable
  // source — it always reaches JS regardless of CORS settings. The
  // X-*-Row-Count headers on the download response can be blocked by
  // the browser when the gateway doesn't list them in
  // Access-Control-Expose-Headers, so we treat the download values as
  // a top-up rather than the primary signal.
  //
  // We deliberately avoid `??` here because the download value is `0`
  // when the headers are hidden, and `0 ?? fallback` would keep the 0.
  const total =
    (job.transformed_row_count ?? 0) ||
    (downloadResult?.totalRows ?? 0) ||
    0;
  const errors =
    errorRows.length ||
    (downloadResult?.errorRows ?? 0) ||
    0;
  // Valid = downloaded header value when exposed; otherwise derive
  // from (total - errors) since the agent's validation_errors list is
  // already authoritative for the failed-row count.
  const valid =
    downloadResult?.validRows && downloadResult.validRows > 0
      ? downloadResult.validRows
      : Math.max(total - errors, 0);
  const hasErrors = errors > 0;

  const mappingColumns = [
    {
      title: "Source column",
      dataIndex: "source",
      key: "source",
      render: (v: string) => (
        <span style={{ fontWeight: 600, color: "#0f172a" }}>{v || "—"}</span>
      ),
    },
    {
      title: "Target field",
      dataIndex: "target",
      key: "target",
      render: (v: string | null) =>
        v ? (
          <Tag color="blue" style={{ borderRadius: 4 }}>
            {v}
          </Tag>
        ) : (
          <Tag style={{ borderRadius: 4 }}>ignored</Tag>
        ),
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      key: "confidence",
      width: 110,
      align: "right" as const,
      render: (v: number) => {
        if (v == null) return "—";
        const pct = Math.round((v || 0) * 100);
        const color =
          v >= 0.85 ? "green" : v >= 0.6 ? "gold" : v > 0 ? "red" : "default";
        return <Tag color={color}>{pct}%</Tag>;
      },
    },
    {
      title: "Master",
      dataIndex: "is_master_data",
      key: "is_master_data",
      width: 80,
      align: "center" as const,
      render: (v: boolean) =>
        v ? (
          <Tag color="purple" style={{ borderRadius: 4 }}>
            yes
          </Tag>
        ) : null,
    },
  ];

  const errorColumns = [
    {
      title: "Row",
      dataIndex: "row",
      key: "row",
      width: 70,
      render: (v: any) => <strong>{v ?? "—"}</strong>,
    },
    {
      title: "Field",
      dataIndex: "field",
      key: "field",
      render: (v: any) => v || "—",
    },
    {
      title: "Type",
      dataIndex: "error_type",
      key: "error_type",
      width: 100,
      render: (v: string) => {
        if (!v) return "—";
        const isInvalid = v.toLowerCase() === "invalid";
        return (
          <Tag color={isInvalid ? "red" : "gold"} style={{ borderRadius: 4 }}>
            {v}
          </Tag>
        );
      },
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (v: any) => v || "—",
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (v: any) => (
        <code
          style={{
            background: "#f1f5f9",
            padding: "1px 6px",
            borderRadius: 4,
            fontSize: 11.5,
            color: "#475569",
          }}
        >
          {v ?? ""}
        </code>
      ),
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: 12,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px 18px",
          background:
            "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
          border: "1px solid #bbf7d0",
          borderRadius: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <CheckCircleFilled style={{ fontSize: 24, color: "#16a34a" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>
              Transform complete
            </div>
            <div style={{ fontSize: 12, color: "#15803d" }}>
              {job.source_file_name} · {total} rows processed
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Tag color="blue" style={{ fontSize: 11.5, padding: "1px 8px" }}>
              {mappingRows.length} columns mapped
            </Tag>
            {errorRows.length > 0 ? (
              <Tag color="error" style={{ fontSize: 11.5, padding: "1px 8px" }}>
                {errorRows.length} validation errors
              </Tag>
            ) : (
              <Tag color="success" style={{ fontSize: 11.5, padding: "1px 8px" }}>
                no errors
              </Tag>
            )}
            {valid > 0 && (
              <Tag color="success" style={{ fontSize: 11.5, padding: "1px 8px" }}>
                {valid} valid rows
              </Tag>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultActiveKey="transformed"
        size="small"
        items={[
          {
            key: "transformed",
            label: (
              <span>
                Transformed Data{" "}
                <Tag style={{ marginLeft: 4 }}>{mappingRows.length}</Tag>
              </span>
            ),
            children:
              mappingRows.length > 0 ? (
                <Table
                  columns={mappingColumns}
                  dataSource={mappingRows}
                  pagination={{ pageSize: 10, size: "small", hideOnSinglePage: true }}
                  size="small"
                  bordered
                  scroll={{ x: "max-content" }}
                />
              ) : (
                <Empty description="No mapping data available" />
              ),
          },
          {
            key: "errors",
            label: (
              <span>
                Validation Errors{" "}
                <Tag
                  color={errorRows.length > 0 ? "red" : "default"}
                  style={{ marginLeft: 4 }}
                >
                  {errorRows.length}
                </Tag>
              </span>
            ),
            children:
              errorRows.length > 0 ? (
                <Table
                  columns={errorColumns}
                  dataSource={errorRows}
                  pagination={{ pageSize: 10, size: "small", hideOnSinglePage: true }}
                  size="small"
                  bordered
                  scroll={{ x: "max-content" }}
                />
              ) : (
                <Empty
                  description={
                    <span style={{ color: "#16a34a" }}>
                      No validation errors — every row is clean.
                    </span>
                  }
                />
              ),
          },
        ]}
      />

      {/* Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <Button icon={<ReloadOutlined />} onClick={onStartOver}>
          Start over
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          {downloadResult ? (
            <>
              <Button onClick={onDownload} icon={<DownloadOutlined />}>
                Download again
              </Button>
              {onClose && (
                <Tooltip title="Close — you can re-upload via the Standard Template tab">
                  <Button type="primary" onClick={onClose}>
                    Done
                  </Button>
                </Tooltip>
              )}
            </>
          ) : (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={onDownload}
            >
              Download Excel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartMapperPanel;
